import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useTranslation } from 'react-i18next';
import {
  IWorkOrder,
  ASSEMBLY_TYPE_COLORS,
  ASSEMBLY_TYPE_LABELS,
  WO_STATUS_LABELS,
  WO_STATUS_COLORS,
} from '@/types/mes';

export interface WorkOrderNodeData {
  workOrder: IWorkOrder;
  onNodeClick?: (woId: string) => void;
}

interface WorkOrderNodeProps {
  data: WorkOrderNodeData;
}

const WorkOrderNode = memo(({ data }: WorkOrderNodeProps) => {
  const { t } = useTranslation('mes');
  const { workOrder, onNodeClick } = data;
  const assemblyColors = ASSEMBLY_TYPE_COLORS[workOrder.assembly_type];
  const statusLabel = WO_STATUS_LABELS[workOrder.status];
  const statusColor = WO_STATUS_COLORS[workOrder.status];

  const completionPercent =
    workOrder.planned_qty > 0
      ? Math.round((workOrder.completed_qty / workOrder.planned_qty) * 100)
      : 0;

  const handleClick = () => {
    if (onNodeClick) {
      onNodeClick(workOrder.id);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`px-4 py-3 rounded-lg border-2 shadow-md cursor-pointer min-w-[200px] max-w-[240px] transition-all hover:shadow-lg ${assemblyColors.bg} ${assemblyColors.border}`}
    >
      {/* Target Handle (top) - receives from parent work orders */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 !bg-gray-400 dark:!bg-gray-500"
      />

      {/* Header: Assembly Type Badge + Status */}
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${assemblyColors.badge}`}>
          {ASSEMBLY_TYPE_LABELS[workOrder.assembly_type]}
        </span>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor}`}>
          {statusLabel}
        </span>
      </div>

      {/* WO Number */}
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
        {workOrder.wo_number}
      </div>

      {/* Product Name */}
      <h4 className={`font-medium text-sm mb-2 line-clamp-2 ${assemblyColors.text}`}>
        {workOrder.bom_product_name}
      </h4>

      {/* Product Code */}
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        {workOrder.bom_product_code}
      </div>

      {/* Progress Bar */}
      <div className="mb-2">
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
          <span>{t('progress', 'Progress')}</span>
          <span>
            {workOrder.completed_qty}/{workOrder.planned_qty} ({completionPercent}%)
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${
              completionPercent >= 100
                ? 'bg-green-500'
                : workOrder.status === 'in_progress'
                ? 'bg-yellow-500'
                : 'bg-gray-400'
            }`}
            style={{ width: `${Math.min(completionPercent, 100)}%` }}
          />
        </div>
      </div>

      {/* BOM Items Count */}
      {workOrder.bom_items && workOrder.bom_items.length > 0 && (
        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <span>
            {workOrder.bom_items.length} {t('materials', 'materials')}
          </span>
        </div>
      )}

      {/* Operators indicator */}
      {workOrder.operators && workOrder.operators.length > 0 && (
        <div className="flex items-center gap-1 mt-1">
          {workOrder.operators.slice(0, 3).map((op: { employee_name: string }, idx: number) => (
            <div
              key={idx}
              className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center"
              title={op.employee_name}
            >
              <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                {op.employee_name.charAt(0).toUpperCase()}
              </span>
            </div>
          ))}
          {workOrder.operators.length > 3 && (
            <span className="text-xs text-gray-500">
              +{workOrder.operators.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Source Handle (bottom) - connects to child work orders */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3 h-3 !bg-gray-400 dark:!bg-gray-500"
      />
    </div>
  );
});

WorkOrderNode.displayName = 'WorkOrderNode';

export default WorkOrderNode;
